import type { SVGProps } from "react";
import type { PillarIconKey } from "@/content/site";

/* Thin, line-style icon set lifted 1:1 from the Claude Design prototype.
   Stroke colour follows `currentColor`; pass width/height or className to size. */

type IconProps = SVGProps<SVGSVGElement>;

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export function IconErp(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...stroke} {...props}>
      <path d="M4 7h16v10H4z" />
      <path d="M4 11h16M9 7v10" />
    </svg>
  );
}

export function IconCompliance(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...stroke} {...props}>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <path d="M4 9h16M9 3v4M15 3v4M9 14l2 2 4-4" />
    </svg>
  );
}

export function IconData(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...stroke} {...props}>
      <path d="M5 19V9M10 19V5M15 19v-7M20 19v-3" />
      <path d="M3 21h18" />
    </svg>
  );
}

export function IconPlatforms(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...stroke} {...props}>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="6" cy="18" r="2.5" />
      <path d="M8.5 6h7M6 8.5v7M18 8.5v7M8.5 18h7" />
    </svg>
  );
}

export function IconDev(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...stroke} {...props}>
      <path d="M8 8l-4 4 4 4M16 8l4 4-4 4M14 5l-4 14" />
    </svg>
  );
}

export function IconAi(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...stroke} {...props}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.2" />
      <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
    </svg>
  );
}

export function IconTools(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...stroke} {...props}>
      <path d="M14.7 6.3a3.6 3.6 0 0 0-4.9 4.2L4 16.3 7.7 20l5.8-5.8a3.6 3.6 0 0 0 4.2-4.9l-2.4 2.4-2-2 1.4-3.4z" />
    </svg>
  );
}

export const PILLAR_ICONS: Record<PillarIconKey, (p: IconProps) => React.JSX.Element> = {
  erp: IconErp,
  compliance: IconCompliance,
  data: IconData,
  platforms: IconPlatforms,
  dev: IconDev,
  ai: IconAi,
  tools: IconTools,
};

export function PillarIcon({
  iconKey,
  ...props
}: IconProps & { iconKey: PillarIconKey }) {
  const C = PILLAR_ICONS[iconKey];
  return <C {...props} />;
}

export function IconCaret(props: IconProps) {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={1.5} {...props}>
      <path d="M3 5l3 3 3-3" />
    </svg>
  );
}

export function IconArrowRight(props: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  );
}

export function IconMenu(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

export function IconClose(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function IconWhatsApp(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9s-.5-.1-.7.2c-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.4-1.5-.9-.8-1.5-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.2-.5.1-.2 0-.4 0-.5 0-.1-.7-1.7-.9-2.3-.2-.5-.4-.5-.6-.5h-.5c-.2 0-.5.1-.7.4-.2.3-.9.9-.9 2.2 0 1.3.9 2.6 1.1 2.7.1.2 1.8 2.7 4.4 3.8.6.3 1.1.4 1.5.5.6.2 1.2.2 1.6.1.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.2-.2-.2-.5-.4z" />
      <path d="M19.4 4.6C17.5 2.6 14.8 1.5 12 1.5 6.2 1.5 1.5 6.2 1.5 12c0 1.8.5 3.6 1.4 5.2L1.5 22.5l5.4-1.4c1.5.8 3.3 1.3 5.1 1.3 5.8 0 10.5-4.7 10.5-10.5 0-2.8-1.1-5.5-3.1-7.3z" />
    </svg>
  );
}

export function IconArrowTextRight() {
  // The prototype uses a literal "→" glyph inside <span class="arrow">.
  return <span className="arrow" aria-hidden>→</span>;
}
